import { Box, Stack, Typography } from "@mui/material";
import { ReactNode, useRef } from "react";
import { useOutsideClick } from "../../../hooks/useOutSideClick";
import { CustomInput } from "../CustomInput";
import { SearchIcon } from "../../images/SearchIcon";
import { TriangleIcon } from "../../images";
import { CustomButton } from "../CustomButton";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  search: string;
  setSearch: (search: string) => void;
  selectedCount: number;
  placeholder: string;
  children: ReactNode;
  closeHandler: () => void;
  submitHandler: () => void;
  cancelHandler: () => void;
  fullwidth?: boolean;
  showCounter?: boolean;
  listMinHeight?: string;
  listMaxHeight?: string;
  withSearch?: boolean;
  isDisabled?: boolean;
};

export const DropdownWithSearchWrapper = ({
  isOpen,
  setIsOpen,
  search,
  setSearch,
  placeholder,
  selectedCount,
  children,
  submitHandler,
  cancelHandler,
  closeHandler,
  showCounter,
  fullwidth,
  listMinHeight,
  listMaxHeight = "254px",
  withSearch = true,
  isDisabled,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    elementRef: dropdownRef,
    onOutsideClick: closeHandler,
    isOpened: isOpen,
    wrapperRef: wrapperRef,
  });

  const hasSelected = selectedCount > 0;

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: "relative",
        width: fullwidth ? "100%" : "156px",
        border: "1px solid #CFCFCF",
        borderRadius: "8px",
        boxSizing: "border-box",
        height: "36px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 16px",
          cursor: isDisabled ? "auto" : "pointer",
          opacity: isDisabled ? "0.6" : "1",
        }}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <Typography component="span" variant="button" color="custom.darkGrey">
          {placeholder}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {hasSelected && showCounter && (
            <Box
              sx={{
                width: "20px",
                height: "20px",
                backgroundColor: "#1B3967",
                color: "white",
                borderRadius: "20px",
                fontSize: "0.75rem",
                lineHeight: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {selectedCount}
            </Box>
          )}

          <Box
            sx={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          >
            <TriangleIcon opacity={isDisabled ? "0.6" : "1"} />
          </Box>
        </Box>
      </Box>

      {isOpen && (
        <Box
          ref={dropdownRef}
          sx={{
            position: "absolute",
            top: "Calc(100% + 8px)",
            left: 0,
            zIndex: 9999,
            backgroundColor: "white",
            boxShadow: "0px 4px 15px 0px #00000040",
            borderRadius: "8px",
            paddingTop: "8px",
            width: fullwidth ? "100%" : "280px",
          }}
        >
          {withSearch && (
            <Box padding="8px 16px">
              <CustomInput
                value={search}
                placeholder="Search"
                prefixIcon={<SearchIcon />}
                onChange={setSearch}
                clearButton
              />
            </Box>
          )}

          <Box
            sx={{
              maxHeight: listMaxHeight,
              "@media (max-height: 900px)": {
                maxHeight: listMinHeight,
              },
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              marginRight: "4px",
            }}
            className="scrollable_container"
          >
            {children}
          </Box>

          <Stack direction="row" gap={1} padding="8px 16px">
            <CustomButton
              onClick={cancelHandler}
              variant="contained"
              textColor="#4C545B"
              fullWidth
              type="button"
              customColor="#F1F1F1"
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={submitHandler}
              variant="contained"
              fullWidth
              type="button"
              customColor="#1C3C6C"
            >
              Apply
            </CustomButton>
          </Stack>
        </Box>
      )}
    </Box>
  );
};
